import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { Layout } from './components/layout/Layout';
import { PRACTICE } from './content/site';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { ServicesPage } from './pages/ServicesPage';
import { ProcessPage } from './pages/ProcessPage';
import { FaqPage } from './pages/FaqPage';
import { ContactPage } from './pages/ContactPage';
import { LegalPage } from './pages/LegalPage';

/** Kept for backwards compatibility with earlier component signatures. */
export type Language = 'en' | 'es';
const META = {
  en: {
    title: 'Pearl Ann Brown | Plastic Surgery & Aesthetic Medicine, New York',
    description: 'Pearl Ann Brown is a plastic surgery and aesthetic medicine practice operated by licensed physicians in New York State. Private consultations by appointment only. English and Spanish.'
  },
  es: {
    title: 'Pearl Ann Brown | Cirugia Plastica y Medicina Estetica, Nueva York',
    description: 'Pearl Ann Brown es una practica de cirugia plastica y medicina estetica operada por medicos con licencia en el Estado de Nueva York. Consultas privadas solo con cita previa. Ingles y espanol.'
  }
};

/**
 * Search-engine identity.
 *
 * Sets the document title, meta description, Open Graph / Twitter tags and a
 * MedicalBusiness JSON-LD block so Google associates this site with the
 * practice name and the published address and email — and with nothing else.
 */
function SeoHead() {
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const meta = META[language];
    document.title = meta.title;
    const setNamed = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const setProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setNamed('description', meta.description);
    setNamed('robots', 'index, follow');
    setNamed('author', PRACTICE.nameTitle);
    setNamed('application-name', PRACTICE.nameTitle);
    setNamed('twitter:card', 'summary_large_image');
    setNamed('twitter:title', meta.title);
    setNamed('twitter:description', meta.description);
    setNamed('twitter:image', PRACTICE.heroImage);
    setProperty('og:type', 'website');
    setProperty('og:site_name', PRACTICE.nameTitle);
    setProperty('og:title', meta.title);
    setProperty('og:description', meta.description);
    setProperty('og:image', PRACTICE.heroImage);
    setProperty('og:locale', language === 'en' ? 'en_US' : 'es_US');

    // Structured data — tells Google the entity behind this site.
    const LD_ID = 'practice-jsonld';
    let script = document.getElementById(LD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = LD_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: PRACTICE.nameTitle,
      alternateName: PRACTICE.name,
      description: meta.description,
      email: PRACTICE.email,
      image: PRACTICE.heroImage,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '9 East 62nd St',
        addressLocality: 'New York',
        addressRegion: 'NY',
        postalCode: '10065',
        addressCountry: 'US'
      },
      availableLanguage: ['English', 'Spanish'],
      medicalSpecialty: 'PlasticSurgery'
    });
  }, [language]);
  return null;
}
export function App() {
  return <LanguageProvider data-id="element-0" data-mp="h6syidy5p981">
      <SeoHead data-id="element-1" data-mp="h8drhz4tok3s" />
      <BrowserRouter data-id="element-2" data-mp="h7vn2f80azks">
        <Routes data-id="element-3" data-mp="h59jeqo8l47u">
          <Route element={<Layout data-id="element-5" data-mp="c8rfft0qd9wk" />} data-id="element-4" data-mp="c89dcfre01gq">
            <Route path="/" element={<Home data-id="element-7" data-mp="ca2kdw5wwnp9" />} data-id="element-6" data-mp="c9amgeouedvt" />
            <Route path="/about" element={<About data-id="element-9" data-mp="19wjdsqbiyh5q" />} data-id="element-8" data-mp="c6eowrgwpr7w" />
            <Route path="/services" element={<ServicesPage data-id="element-11" data-mp="19tx5upkq1zmz" />} data-id="element-10" data-mp="19uotne82ut2p" />
            <Route path="/process" element={<ProcessPage data-id="element-13" data-mp="14y84qht20aok" />} data-id="element-12" data-mp="14x6jjkibq7t4" />
            <Route path="/faq" element={<FaqPage data-id="element-15" data-mp="zxk3z4f0wfh5" />} data-id="element-14" data-mp="14uu1768nlvfj" />
            <Route path="/contact" element={<ContactPage data-id="element-17" data-mp="zw0mxbljm0eb" />} data-id="element-16" data-mp="zx2848w9w39r" />
            <Route path="/privacy" element={<LegalPage kind="privacy" data-id="element-19" data-mp="vx059wesx3sw" />} data-id="element-18" data-mp="vw8o3qlyb8m5" />
            <Route path="/terms" element={<LegalPage kind="terms" data-id="element-21" data-mp="3knt0cx1gsnnc" />} data-id="element-20" data-mp="vybam0nwajm4" />
            <Route path="/disclaimer" element={<LegalPage kind="disclaimer" data-id="element-23" data-mp="3gmpk49fi8pll" />} data-id="element-22" data-mp="3koudd721jk7p" />
            <Route path="/accessibility" element={<LegalPage kind="accessibility" data-id="element-25" data-mp="1w5gf4u6bo25u" />} data-id="element-24" data-mp="3gl64ml1oaikv" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-27" data-mp="2058qfen3omew" />} data-id="element-26" data-mp="2060bvwmzh3rg" />
          </Route>
        </Routes>
      </BrowserRouter>
    </LanguageProvider>;
}